import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost } from '../../api/axios-handler';
import useAuth from '../utils/useAuth';
import { usePostCallPolling } from './usePostCallPolling';
import rollbar from '../../utils/rollbar';
import { readSseStream, SseFrame, sseHeaders } from '../../utils/sse';

export const FI_SEARCH_PROGRESS_EVENT = 'fi-search-progress' as const;

export interface FiSearchProgressPayload {
  context: 'fi_search_progress';
  key: string;
  message: string;
  tool?: string;
  query?: string;
}

export interface Notification {
  context: 'engagement_check_in' | 'updated_call' | 'updated_offers' | 'call_passed' | 'fi_search_progress';
  request_id?: number;
  key?: string;
  message?: string;
  tool?: string;
  query?: string;
}

const sseEnabled = process.env.REACT_APP_NOTIFICATIONS_SSE === 'true';

// The server sends `: keepalive` every 20s. Three missed in a row means the
// socket is dead even if it never got a FIN (network switch, phone sleep).
export const STREAM_HEARTBEAT_MS = 20_000;
export const STREAM_IDLE_TIMEOUT_MS = 3 * STREAM_HEARTBEAT_MS;
// After this many failed attempts in a row, use the long-poll instead.
export const MAX_STREAM_FAILURES = 5;
// A 401 or 403 is retried this many times, with backoff, before the user is
// logged out. One rejection can be transient (a WAF block, a token refresh
// racing the request).
export const MAX_AUTH_RETRIES = 3;
export const INITIAL_FAILURE_DELAY_MS = 1_000;
export const MAX_FAILURE_DELAY_MS = 30_000;
// Acks are batched: a burst of notifications sends one ack for the newest.
export const ACK_DEBOUNCE_MS = 1_000;
// A 4xx other than 401, 403 and 404 (a WAF block, a 429) is retried after
// this long, or sooner if the token changes.
export const CLIENT_ERROR_WAIT_MS = 30_000;

const fetchNotifications = async (userId?: number) => {
  if (userId) {
    return await axiosGet(`/users/${userId}/notification`, {}).then((response) => response.data);
  }
};

// ack=1 asks the server to keep each row until we ack it, so a stream whose
// client has already gone can't consume a notification. last_event_id acks
// everything through that id when a new stream opens. It's a query parameter
// rather than a Last-Event-ID header so it needs no CORS allow-list change.
export function streamUrl(userId: number, lastEventId: number): string {
  const domain = process.env.REACT_APP_DOMAIN_NAME ?? '';
  const version = process.env.REACT_APP_API_VERSION2 ?? '/api/v2';
  const query = lastEventId > 0 ? `ack=1&last_event_id=${lastEventId}` : 'ack=1';
  return `${domain}${version}/users/${userId}/notifications/stream?${query}`;
}

export interface NotificationAcker {
  // False for an id this tab has already handled. Ack delivery is
  // at-least-once, so a reconnect can resend rows.
  isNew: (id: number) => boolean;
  handled: (id: number) => void;
  // Newest handled id, sent as last_event_id on the next stream.
  lastSeen: () => number;
  // Ack now instead of waiting for the debounce.
  flush: () => void;
}

export function createNotificationAcker(
  send: (lastEventId: number) => Promise<unknown>,
  debounceMs: number = ACK_DEBOUNCE_MS,
): NotificationAcker {
  let seen = 0;
  let acked = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    clearTimeout(timer);
    timer = undefined;
    if (seen <= acked) return;
    const id = seen;
    send(id).then(
      () => {
        acked = Math.max(acked, id);
      },
      // Unacked rows stay on the server and are acked by last_event_id when
      // the next stream opens.
      (error) => console.warn('[notifications] ack failed', id, error),
    );
  };
  return {
    isNew: (id) => id > seen,
    handled: (id) => {
      if (id <= seen) return;
      seen = id;
      if (timer === undefined) timer = setTimeout(flush, debounceMs);
    },
    lastSeen: () => seen,
    flush,
  };
}

function guestToken(): string | null {
  try {
    return localStorage.getItem('authorization_guest_token');
  } catch {
    return null;
  }
}

/**
 * How one stream attempt ended. `healthy` means the stream got at least one
 * keepalive, so the server side was working when it ended.
 */
export type StreamOutcome =
  | { kind: 'bye' }
  | { kind: 'closed'; healthy: boolean }
  // A 200 that isn't text/event-stream, like a CDN's SPA fallback page.
  | { kind: 'not-event-stream' }
  | { kind: 'failed'; status: number; healthy: boolean };

export interface StreamBackoff {
  failures: number;
  delayMs: number;
  // 401s and 403s in a row. Counted apart from failures so they end in a
  // logout, not the long-poll.
  authFailures: number;
}

export const INITIAL_STREAM_BACKOFF: StreamBackoff = {
  failures: 0,
  delayMs: INITIAL_FAILURE_DELAY_MS,
  authFailures: 0,
};

function backoffDelay(delayMs: number, random: () => number): number {
  return Math.min(delayMs * (1 + random() * 0.5), MAX_FAILURE_DELAY_MS);
}

export type StreamStep =
  | { kind: 'reconnect'; delayMs: number }
  // maxMs null waits until the effect restarts, which a token change does.
  | { kind: 'wait-for-token'; maxMs: number | null }
  // Retries are used up. Hand the status to the app's global auth error
  // handling, then wait for a new token.
  | { kind: 'unauthorized'; status: number }
  | { kind: 'fallback' };

/** Decide what the loop does after a stream attempt ends. */
export function nextStreamStep(
  outcome: StreamOutcome,
  backoff: StreamBackoff,
  random: () => number = Math.random,
): { step: StreamStep; backoff: StreamBackoff } {
  if (outcome.kind === 'bye') {
    // The server ends the stream when the token stops being valid (logout,
    // expiry, inactivity, disabled account). The same token would only get
    // a 403.
    return { step: { kind: 'wait-for-token', maxMs: null }, backoff };
  }
  if (outcome.kind === 'not-event-stream') {
    // Something other than the stream route answered. Retrying won't change that.
    return { step: { kind: 'fallback' }, backoff };
  }
  if (outcome.healthy) {
    // A stream that was up and then dropped is not a failure. Reconnect soon,
    // with jitter so tabs don't all reconnect together after a deploy.
    return { step: { kind: 'reconnect', delayMs: 500 + random() * 1500 }, backoff: INITIAL_STREAM_BACKOFF };
  }
  const status = outcome.kind === 'failed' ? outcome.status : 0;
  if (status === 401 || status === 403) {
    const authFailures = backoff.authFailures + 1;
    if (authFailures > MAX_AUTH_RETRIES) {
      return { step: { kind: 'unauthorized', status }, backoff: { ...backoff, authFailures } };
    }
    return {
      step: { kind: 'reconnect', delayMs: backoffDelay(backoff.delayMs, random) },
      backoff: { ...backoff, authFailures, delayMs: Math.min(backoff.delayMs * 2, MAX_FAILURE_DELAY_MS) },
    };
  }
  if (status === 404) {
    // The stream route isn't deployed here.
    return { step: { kind: 'fallback' }, backoff };
  }
  const next: StreamBackoff = {
    failures: backoff.failures + 1,
    delayMs: Math.min(backoff.delayMs * 2, MAX_FAILURE_DELAY_MS),
    authFailures: 0,
  };
  if (next.failures >= MAX_STREAM_FAILURES) {
    return { step: { kind: 'fallback' }, backoff: next };
  }
  if (status >= 400 && status < 500) {
    return { step: { kind: 'wait-for-token', maxMs: CLIENT_ERROR_WAIT_MS }, backoff: next };
  }
  // A 5xx, a network error (0), or a stream that closed before its first
  // keepalive.
  return { step: { kind: 'reconnect', delayMs: backoffDelay(backoff.delayMs, random) }, backoff: next };
}

export interface ReadNotificationStreamOptions {
  url: string;
  token: string | null;
  controller: AbortController;
  // eventId is the row id when the server sends one (ack mode).
  onNotification: (data: Notification, eventId?: number) => void;
  idleTimeoutMs?: number;
}

/**
 * Open the notification stream and read it until it ends. It never throws:
 * every way it can end comes back as a StreamOutcome. On the way out it
 * aborts the controller and cancels the body, so no stream is left open.
 */
export async function readNotificationStream({
  url,
  token,
  controller,
  onNotification,
  idleTimeoutMs = STREAM_IDLE_TIMEOUT_MS,
}: ReadNotificationStreamOptions): Promise<StreamOutcome> {
  let healthy = false;
  let idle = false;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  // Without this, a socket that stops sending without a FIN leaves read()
  // pending forever and the tab never reconnects.
  const armIdleTimer = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      idle = true;
      controller.abort();
    }, idleTimeoutMs);
  };
  const onFrame = (frame: SseFrame): boolean => {
    if (frame.kind === 'comment') {
      // `: open` is sent before the first status check, so only a keepalive
      // shows the server side is working.
      if (frame.text === 'keepalive') healthy = true;
      return false;
    }
    if (frame.event === 'bye') return true;
    if (frame.event !== 'notification' || !frame.data || frame.data === 'null') return false;
    let data: Notification;
    try {
      data = JSON.parse(frame.data) as Notification;
    } catch (error) {
      console.error('[notifications] could not parse a stream frame', frame.data, error);
      return false;
    }
    const eventId = frame.id !== undefined && /^\d+$/.test(frame.id) ? Number(frame.id) : undefined;
    try {
      onNotification(data, eventId);
    } catch (error) {
      // The row is acked like any other, so leave a trace. Keep reading:
      // the rows after it in this chunk still need handling.
      console.error('[notifications] could not handle a notification', data, error);
      rollbar.error('notifications: could not handle a notification', error as Error, { notification: data });
    }
    return false;
  };
  try {
    armIdleTimer();
    const response = await fetch(url, {
      method: 'GET',
      headers: sseHeaders(token),
      credentials: 'include',
      signal: controller.signal,
    });
    if (!response.ok || !response.body) {
      return { kind: 'failed', status: response.status, healthy };
    }
    if (!(response.headers.get('content-type') ?? '').includes('text/event-stream')) {
      response.body.cancel().catch(() => undefined);
      return { kind: 'not-event-stream' };
    }
    const closed = await readSseStream(response.body, { onFrame, onChunk: armIdleTimer });
    return closed ? { kind: 'closed', healthy } : { kind: 'bye' };
  } catch (error) {
    if (idle) console.warn(`[notifications] no stream data for ${idleTimeoutMs}ms, reconnecting`);
    else if (!controller.signal.aborted) console.warn('[notifications] stream failed', error);
    return { kind: 'failed', status: 0, healthy };
  } finally {
    clearTimeout(idleTimer);
    controller.abort();
  }
}

export const useNotifications = (options?: { onSuccess?: (data: Notification | null) => void }) => {
  const { user, authToken } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const { startPolling } = usePostCallPolling(user?.caller_role_id);
  const onSuccessRef = useRef(options?.onSuccess);
  onSuccessRef.current = options?.onSuccess;
  const [sseFallback, setSseFallback] = useState(false);

  const deliver = (data: Notification | null) => {
    onSuccessRef.current?.(data);
    if (data?.context === 'fi_search_progress') {
      // A Fi search sends a burst of these. They only drive the progress UI,
      // so they don't refetch the call lists.
      if (typeof data.message === 'string') {
        window.dispatchEvent(
          new CustomEvent<FiSearchProgressPayload>(FI_SEARCH_PROGRESS_EVENT, {
            detail: data as FiSearchProgressPayload,
          }),
        );
      }
    } else {
      queryClient.invalidateQueries(['activeCalls']);
      // Also covers call_passed and updated_offers.
      queryClient.invalidateQueries(['callRequests']);
      // Always invalidate postcall list to ensure "How'd it go?" button appears after call ends
      queryClient.invalidateQueries(['postCallList']);
      if (data?.context === 'engagement_check_in') queryClient.invalidateQueries(['reflections']);
      if (data?.context === 'updated_call') {
        // Start polling for postcall metadata
        // postcall_metadata is created asynchronously via fire_and_forget in settle_call,
        // so we poll every 15 seconds until it's ready or 5 minutes have elapsed
        startPolling();
      }
    }
    if (!sseEnabled || sseFallback) queryClient.invalidateQueries(['poll']);
  };
  // The stream loop outlives renders, so it calls the latest deliver (and the
  // latest startPolling, which closes over caller_role_id) through this ref.
  const deliverRef = useRef(deliver);
  deliverRef.current = deliver;
  // Kept across stream restarts (a token change restarts the effect), so a
  // new stream still acks and dedupes against what this tab has handled.
  const ackerRef = useRef<{ userId: number; acker: NotificationAcker } | null>(null);

  // SSE stream. authToken is a dependency, so a new token restarts the loop.
  // That is what ends a wait for a new token.
  useEffect(() => {
    if (!sseEnabled || sseFallback || userId == null) return;
    if (ackerRef.current?.userId !== userId) {
      ackerRef.current = {
        userId,
        acker: createNotificationAcker((lastEventId) =>
          axiosPost(`/users/${userId}/notifications/ack`, { last_event_id: lastEventId }),
        ),
      };
    }
    const { acker } = ackerRef.current;
    let cancelled = false;
    let controller: AbortController | null = null;
    const wakers = new Set<() => void>();
    // Resolves after ms, or on cleanup. With null it waits for cleanup only.
    const pause = (ms: number | null) =>
      new Promise<void>((resolve) => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const wake = () => {
          clearTimeout(timer);
          wakers.delete(wake);
          resolve();
        };
        wakers.add(wake);
        if (ms !== null) timer = setTimeout(wake, ms);
      });
    const loop = async () => {
      let backoff = INITIAL_STREAM_BACKOFF;
      while (!cancelled) {
        // Read the token once, so the one sent is the one this attempt used.
        const token = authToken ?? guestToken();
        controller = new AbortController();
        const outcome = await readNotificationStream({
          url: streamUrl(userId, acker.lastSeen()),
          token,
          controller,
          onNotification: (data, eventId) => {
            if (eventId !== undefined) {
              if (!acker.isNew(eventId)) return;
              // Ack it even if deliver throws: the reader reports that error,
              // and an unacked row would come back on every reconnect.
              acker.handled(eventId);
            }
            deliverRef.current(data);
          },
        });
        if (cancelled) return;
        const next = nextStreamStep(outcome, backoff);
        backoff = next.backoff;
        if (next.step.kind === 'fallback') {
          console.warn('[notifications] stream unavailable, falling back to the long-poll', outcome);
          setSseFallback(true);
          return;
        }
        if (next.step.kind === 'unauthorized') {
          // Same handling as any query that gets a 403: the QueryClient's
          // default onError logs the user out. Then stay stopped until the
          // token changes, instead of retrying into the same rejection.
          console.error(`[notifications] stream rejected with ${next.step.status} after retries, logging out`);
          rollbar.error('notifications: stream rejected after retries, logging out', {
            status: next.step.status,
            attempts: next.backoff.authFailures,
          });
          queryClient.getDefaultOptions().queries?.onError?.({ response: { status: next.step.status } });
          await pause(null);
          continue;
        }
        await pause(next.step.kind === 'reconnect' ? next.step.delayMs : next.step.maxMs);
      }
    };
    void loop();
    return () => {
      cancelled = true;
      controller?.abort();
      wakers.forEach((wake) => wake());
      acker.flush();
    };
  }, [sseFallback, userId, authToken, queryClient]);

  // long poll
  return useQuery(['poll', userId], () => fetchNotifications(userId), {
    retry: true,
    retryDelay: 1000 * 30,
    enabled: user !== undefined && (!sseEnabled || sseFallback),
    onSuccess: (data) => {
      deliver(data);
    },
  });
};
